import type { Rect } from '../../../engine/hex/types';
import { IconRegistry } from '../../../ui/overlay/assets/IconRegistry';
import type { ModalTheme } from '../ModalTheme';
import type { HitTarget, HouseGridEntry, MeasureResult, UiItem } from './Types';

export class HouseGridItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    private cellRects: Rect[] = [];
    private columns = 3;
    private gap = 8;
    private padding = 6;
    private cellHeight = 0;

    constructor(private items: HouseGridEntry[]) {}

    measure(_ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        this.columns = theme.houseGrid.columns;
        this.gap = theme.houseGrid.gap;
        this.padding = theme.houseGrid.padding;
        this.cellHeight = theme.houseGrid.cellHeight;
        const rows = Math.ceil(this.items.length / this.columns);
        const height = rows * this.cellHeight + this.gap * Math.max(0, rows - 1);
        return { w: maxW, h: height };
    }

    layout(x: number, y: number, w: number, _theme: ModalTheme): void {
        const rows = Math.ceil(this.items.length / this.columns);
        const height = rows * this.cellHeight + this.gap * Math.max(0, rows - 1);
        this.rect = { x, y, w, h: height };
        const cellW = (w - this.gap * Math.max(0, this.columns - 1)) / this.columns;
        this.cellRects = [];
        for (let index = 0; index < this.items.length; index += 1) {
            const row = Math.floor(index / this.columns);
            const col = index % this.columns;
            const cx = x + col * (cellW + this.gap);
            const cy = y + row * (this.cellHeight + this.gap);
            this.cellRects.push({ x: cx, y: cy, w: cellW, h: this.cellHeight });
        }
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, _icons?: IconRegistry): void {
        ctx.textBaseline = 'top';
        this.cellRects.forEach((cell, index) => {
            const entry = this.items[index];
            if (!entry) return;
            ctx.fillStyle = theme.colors.houseTileFill;
            ctx.beginPath();
            ctx.rect(cell.x, cell.y, cell.w, cell.h);
            ctx.fill();

            if (entry.warning) {
                ctx.fillStyle = theme.colors.houseTileWarnFill;
                ctx.fillRect(cell.x, cell.y, cell.w, cell.h);
            }

            ctx.strokeStyle = entry.selected
                ? theme.colors.houseTileSelectedStroke
                : theme.colors.houseTileStroke;
            ctx.lineWidth = entry.selected ? 2 : 1;
            ctx.strokeRect(cell.x, cell.y, cell.w, cell.h);

            ctx.font = theme.fonts.body;
            ctx.fillStyle = theme.colors.bodyText;
            ctx.textAlign = 'left';
            ctx.fillText(entry.title, cell.x + this.padding, cell.y + this.padding);

            ctx.font = theme.fonts.muted;
            ctx.fillStyle = theme.colors.mutedText;
            ctx.fillText(
                entry.occupants,
                cell.x + this.padding,
                cell.y + this.padding + theme.lineHeights.body,
            );
            ctx.fillText(
                entry.foodLabel,
                cell.x + this.padding,
                cell.y + this.padding + theme.lineHeights.body + theme.lineHeights.muted,
            );
            ctx.fillText(
                entry.warmthLabel,
                cell.x + this.padding,
                cell.y + this.padding + theme.lineHeights.body + theme.lineHeights.muted * 2,
            );
            ctx.fillText(
                entry.woodLabel,
                cell.x + this.padding,
                cell.y + this.padding + theme.lineHeights.body + theme.lineHeights.muted * 3,
            );
            ctx.fillText(
                entry.charcoalLabel,
                cell.x + this.padding,
                cell.y + this.padding + theme.lineHeights.body + theme.lineHeights.muted * 4,
            );
        });
    }

    collectHitTargets(out: HitTarget[], scrollY: number): void {
        this.cellRects.forEach((rect, index) => {
            const entry = this.items[index];
            if (!entry) return;
            out.push({
                id: entry.id,
                enabled: true,
                rect: { x: rect.x, y: rect.y - scrollY, w: rect.w, h: rect.h },
            });
        });
    }
}
